import React, { FC, FormEventHandler, useState } from 'react';
import Svg from 'svg-inline-react';
import { TextField } from '@src/components/TextField';
import { Button } from '@src/components/Button';
import { Terms } from '@src/components/Terms';
import { CountryLocationDropdown } from '@src/components/CountryLocationDropdown';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { trackAddCardDetails, trackEvent } from '@src/metrics';
import { UpdateRecruiterInput } from '@src/graphql/generated';
import LogoSvg from '../logo.svg';
import { UpdateProfileDataProps } from './props';
import './styles.scss';

export const ProfileData: FC<UpdateProfileDataProps> = ({
  recruiter,
  updateRecruiterInput,
  onContinue,
  onUpdateRecruiterInput,
}) => {
  const [areTermsAccepted, setAreTermsAccepted] = useState(false);

  const setUpdateRecruiterInput = <TKey extends keyof UpdateRecruiterInput>(
    key: TKey,
    value: UpdateRecruiterInput[TKey],
  ) => {
    onUpdateRecruiterInput({
      ...updateRecruiterInput,
      [key]: value,
    });
  };

  const onContinueClick: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();

    onContinue();

    trackEvent({
      context: MetricContext.FINISH_RECRUITER_PROFILE,
      interaction: MetricAction.OPEN_UPDATE_PASSWORD_VIEW,
    });
  };

  return (
    <div className="finish-recruiter-modal-profile-data">
      <div className="finish-recruiter-modal-header">
        <div className="finish-recruiter-modal-header-title">
          Finish completing
          <br />
          <span>your profile.</span>
        </div>

        <div className="finish-recruiter-modal-header-info">
          Please review and confirm <br />
          <span>your profile details</span>&nbsp;to proceed.
        </div>

        <Svg src={LogoSvg} />
      </div>

      <form className="finish-recruiter-modal-content" onSubmit={onContinueClick}>
        <div>
          <TextField
            defaultValue={updateRecruiterInput.fullName}
            label="Full name"
            onBlur={() => trackAddCardDetails('Full Name')}
            onChange={newFullName => setUpdateRecruiterInput('fullName', newFullName)}
            wrapperClassName="profile-data-input"
          />

          <TextField
            defaultValue={recruiter.email}
            isDisabled={true}
            label="Email"
            wrapperClassName="profile-data-input"
          />

          <TextField
            defaultValue={updateRecruiterInput.phoneNumber}
            label="Phone number"
            onBlur={() => trackAddCardDetails('Phone Number')}
            onChange={newPhoneNumber => setUpdateRecruiterInput('phoneNumber', newPhoneNumber)}
            wrapperClassName="profile-data-input"
          />
        </div>

        <div>
          <TextField
            defaultValue={updateRecruiterInput.positionTitle}
            fullWidth
            label="Position title"
            onBlur={() => trackAddCardDetails('Position Title')}
            onChange={newTitle => setUpdateRecruiterInput('positionTitle', newTitle)}
            wrapperClassName="profile-data-input"
          />

          <div className="finish-recruiter-modal-profile-data__location-input">
            <CountryLocationDropdown
              defaultValue={
                recruiter.country ? { label: recruiter.country.longName, value: recruiter.country.id } : undefined
              }
              onSelect={option => setUpdateRecruiterInput('countryId', option.value)}
              placeholder="Country"
            />
          </div>

          <Terms checked={areTermsAccepted} onChange={() => setAreTermsAccepted(!areTermsAccepted)} />

          <Button
            className="finish-recruiter-modal-profile-data__continue-button"
            isDisabled={!areTermsAccepted || Object.values(updateRecruiterInput).filter(value => !value).length > 0}
            type="submit"
          >
            Proceed
          </Button>
        </div>
      </form>
    </div>
  );
};
